import React, { createContext, ReactNode, useContext, useMemo } from "react";
import { useAuth as useClerkAuth, useUser } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

// Define user types - User can be Clerk's user object augmented with our status
interface AppUser {
  id?: string; // Clerk user ID is a string
  email?: string | null;
  username?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  imageUrl?: string;
  subscriptionStatus?: "inactive" | "active" | "mentorship" | null;
}

interface AuthContextType {
  user: AppUser | null;
  isLoading: boolean; // Overall loading state (Clerk + subscription)
  isSubscriptionLoading: boolean;
  error: Error | null; // For subscription fetch error
  isMentorshipMember: boolean;
  isPremiumMember: boolean;
  logout: () => Promise<void>;
  isSignedIn: boolean | undefined; // Directly from Clerk
}

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isSignedIn, isLoaded: isClerkLoaded, signOut } = useClerkAuth();
  const { user: clerkUser, isLoaded: isUserLoaded } = useUser();

  // We no longer rely on any backend membership endpoint – Clerk sign-in is the sole source of truth
  const isSubscriptionLoading = false;
  const subscriptionError: Error | null = null;

  // Memoise combined user to avoid recreating the object on every render
  const combinedUser: AppUser | null = useMemo(() => {
    if (!clerkUser) return null;
    return {
      id: clerkUser.id,
      email: clerkUser.primaryEmailAddress?.emailAddress ?? null,
      username: clerkUser.username,
      firstName: clerkUser.firstName,
      lastName: clerkUser.lastName,
      imageUrl: clerkUser.imageUrl,
      subscriptionStatus: isSignedIn ? "mentorship" : null,
    } satisfies AppUser;
  }, [clerkUser, isSignedIn]);

  // No additional effect required – Clerk alone drives auth state.

  const isLoading = !isClerkLoaded || !isUserLoaded;

  // Any signed‑in Clerk user is considered a mentorship member under the new logic
  const isMentorshipMember = !!isSignedIn;
  const isPremiumMember = !!isSignedIn;

  const logout = async () => {
    try {
      await signOut();
      queryClient.clear(); // Clear any cached queries on logout (not strictly necessary)
      toast({
        title: "Logged out successfully",
        description: "You have been logged out of your account.",
      });
    } catch (error) {
      toast({
        title: "Logout failed",
        description: "There was an error logging out. Please try again.",
        variant: "destructive",
      });
      console.error("Clerk sign out error:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user: combinedUser,
        isLoading,
        isSubscriptionLoading,
        error: subscriptionError as Error | null,
        isMentorshipMember,
        isPremiumMember,
        logout,
        isSignedIn,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Hook to use the auth context
export function useAppAuth() { // Renamed to avoid conflict with Clerk's useAuth
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAppAuth must be used within an AuthProvider");
  }
  return context;
}